import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import store, { State as RootState } from '@/store'
import { UserProfilePrice } from '@/types/PriceType';
import { createPrice, deletePrice, fetchPrices, sortPrices, updatePrice, uploadPriceCover, deletePriceCover } from '@/data/requests/profilePrices';

export type State = {
    items: UserProfilePrice[],
}

export type Mutations<S = State> = {
    setPrices(state: S, { items }: { items: UserProfilePrice[] }): void,
}

type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

export interface Actions {
    loadPrices(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>,
    createPrice(
        { dispatch }: AugmentedActionContext,
        payload: Pick<UserProfilePrice, 'title' | 'description' | 'price' | 'currency'>
    ): Promise<any>,
    updatePrice(
        { dispatch }: AugmentedActionContext,
        payload: { id: string, data: Pick<UserProfilePrice, 'title' | 'description' | 'price' | 'currency'> }
    ): Promise<any>,
    deletePrice(
        { dispatch }: AugmentedActionContext,
        payload: { id: string }
    ): Promise<any>,
    sortPrices(
        { commit, getters }: AugmentedActionContext,
        payload: { items: UserProfilePrice[] }
    ): Promise<any>,
    uploadCover(
        { dispatch }: AugmentedActionContext,
        payload: { id: string, file: File, onProgress?: (p: number) => void }
    ): Promise<any>,
    deleteCover(
        { dispatch }: AugmentedActionContext,
        payload: { id: string }
    ): Promise<any>,
}

export type Getters = {
    prices(state: State): UserProfilePrice[],
}

const state: State = {
    items: [],
}

const getters: GetterTree<State, RootState> & Getters = {
    prices: state => state.items,
}

const mutations: MutationTree<State> & Mutations = {
    setPrices(state, { items }) {
        state.items = items || [];
    },
}

const actions: ActionTree<State, RootState> & Actions = {
    loadPrices({ commit }: AugmentedActionContext): Promise<any> {
        return new Promise((resolve: any, reject) => {
            fetchPrices((items) => {
                commit('setPrices', { items });
                resolve(items);
            }, (type, data) => reject({ type, data }));
        })
    },

    createPrice({ dispatch }, payload): Promise<any> {
        return new Promise((resolve: any, reject) => {
            createPrice(payload, () => {
                dispatch('loadPrices');
                resolve(true);
            }, (type, data) => reject({ type, data }));
        })
    },

    updatePrice({ dispatch }, { id, data }): Promise<any> {
        return new Promise((resolve: any, reject) => {
            updatePrice(id, data, () => {
                dispatch('loadPrices');
                resolve(true);
            }, (type, data) => reject({ type, data }));
        })
    },

    deletePrice({ dispatch }, { id }): Promise<any> {
        return new Promise((resolve: any, reject) => {
            deletePrice(id, () => {
                dispatch('loadPrices');
                resolve(true);
            }, (type, data) => reject({ type, data }));
        })
    },

    sortPrices({ commit, getters }, { items }): Promise<any> {
        const temp = getters.prices;
        commit('setPrices', { items: (items.length === 0 ? temp : items) });
        return new Promise((resolve: any, reject) => {
            sortPrices(items, () => resolve(), (type, data) => {
                commit('setPrices', { items: temp });
                reject({ type, data });
            })
        })
    },

    uploadCover({ dispatch }, { id, file, onProgress }): Promise<any> {
        return new Promise((resolve: any, reject) => {
            uploadPriceCover(id, file, () => {
                dispatch('loadPrices');
                resolve(true);
            }, (type, data) => reject({ type, data }), onProgress);
        })
    },

    deleteCover({ dispatch }, { id }): Promise<any> {
        return new Promise((resolve: any, reject) => {
            deletePriceCover(id, () => {
                dispatch('loadPrices');
                resolve(true);
            }, (type, data) => reject({ type, data }));
        })
    },
}

export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
    > & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const ProfilePricesModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}


